import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf } from 'lucide-react';

/**
 * ESGCaffe intro overlay — cinematic first-paint gate ported from Breww.
 * Light theme (site is light-mode-only).
 *
 * Choreography:
 *   0.0s   overlay full-opacity, ESG leaf logo scales 0.6 → 1.0
 *   0.7s   logo balloons out (scale ~11×) and fades
 *   1.1s   overlay cross-fades to transparent, dispatches `esg:intro-done`
 *   ≤4.0s  hard fallback if events didn't fire
 *
 * Plays once per session (sessionStorage flag). `prefers-reduced-motion` skips
 * to a plain fade + immediate event dispatch.
 */
const SESSION_KEY = 'esg:intro-played';

const IntroOverlay = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(SESSION_KEY) !== '1';
    } catch (_) {
      return true;
    }
  });
  const firedRef = useRef(false);

  const fireDone = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('esg:intro-done'));
  };

  useEffect(() => {
    if (!visible) {
      fireDone();
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Hard 4s fallback — never let the page stay locked
    const fb = window.setTimeout(() => setVisible(false), 4000);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(fb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reduce = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <AnimatePresence onExitComplete={fireDone}>
      {visible && (
        <motion.div
          key="esg-intro"
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 50%, #ecfeff 100%)',
          }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0.2 : 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            initial={{ scale: reduce ? 1 : 0.6, opacity: 0 }}
            animate={reduce
              ? { scale: 1, opacity: 1 }
              : { scale: [0.6, 1, 1, 11], opacity: [0, 1, 1, 0] }}
            transition={reduce
              ? { duration: 0.2 }
              : { duration: 1.4, times: [0, 0.35, 0.55, 1], ease: [0.6, 0, 0.4, 1] }}
            onAnimationComplete={() => setVisible(false)}
            className="flex items-center justify-center"
          >
            <div
              className="flex items-center justify-center rounded-3xl"
              style={{
                width: 96,
                height: 96,
                background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                boxShadow: '0 20px 60px rgba(16, 185, 129, 0.35)',
              }}
            >
              <Leaf className="h-12 w-12" style={{ color: '#ffffff' }} strokeWidth={1.6} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroOverlay;
